import React from 'react';

const Rules: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">📋 Game Rules</h2>
        <p className="text-gray-300">Complete guide to Token Launch League gameplay</p>
      </div>

      <div className="grid gap-6">
        {/* Game Overview */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">🎯 Game Overview</h3>
          <div className="space-y-4 text-gray-300">
            <p>
              Token Launch League is a competitive prediction game where players compete to predict 
              the Final Diluted Valuation (FDV) 24 hours after upcoming token generation events (TGEs). 
              Players use chips to place bets on their predictions, with accuracy determining points earned.
            </p>
            <p>
              The player with the highest total points at the end of all TGEs wins. 
              In case of ties, the player with the most chips remaining wins.
            </p>
          </div>
        </div>

        {/* Starting Configuration */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">🎰 Starting Configuration</h3>
          <div className="space-y-3 text-gray-300">
            <div className="flex items-center">
              <span className="mr-2">•</span>
              <span>Each team member starts with <span className="text-primary font-bold">100 chips</span></span>
            </div>
            <div className="flex items-center">
              <span className="mr-2">•</span>
              <span>All predictions and bets use these chips</span>
            </div>
            <div className="flex items-center">
              <span className="mr-2">•</span>
              <span>Individual competition - highest points wins</span>
            </div>
            <div className="flex items-center">
              <span className="mr-2">•</span>
              <span>8 team members: Vicki, Dan, Francis, Matt, Marcou, Noah, Andrew, Nitin</span>
            </div>
          </div>
        </div>

        {/* Core Rules */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">📜 Core Rules</h3>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <span className="mr-2 mt-1">🎯</span>
                  <div>
                    <span className="font-bold text-white">Predictions:</span> Predict FDV values for upcoming TGEs
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 mt-1">🎰</span>
                  <div>
                    <span className="font-bold text-white">Betting:</span> Place chips on predictions to increase potential winnings
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 mt-1">📊</span>
                  <div>
                    <span className="font-bold text-white">Scoring:</span> Points awarded based on prediction accuracy
                  </div>
                </div>
              </div>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start">
                  <span className="mr-2 mt-1">💰</span>
                  <div>
                    <span className="font-bold text-white">Settlements:</span> Predict the FDV value 24 hours after TGE launch - that's what you're betting on
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 mt-1">⏰</span>
                  <div>
                    <span className="font-bold text-white">Deadlines:</span> Predictions must be submitted before TGE launch
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="mr-2 mt-1">🏆</span>
                  <div>
                    <span className="font-bold text-white">Winning:</span> Player with highest total points wins (ties broken by chips remaining)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TGE System - CACHE BUST VERSION */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">🚀 TGE System</h3>
          <div className="space-y-4">
            <p className="text-gray-300">
              Current TGEs with correct project names and dates (Updated July 28, 2025):
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Unite:</span>
                  <span className="text-primary">July 30, 2025</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">RHEA:</span>
                  <span className="text-primary">July 30, 2025</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Towns:</span>
                  <span className="text-primary">August 5, 2025</span>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Caesar:</span>
                  <span className="text-primary">August 16, 2025</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Rivalz:</span>
                  <span className="text-primary">TBD</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">OpenLedger:</span>
                  <span className="text-primary">TBD</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Gaia:</span>
                  <span className="text-primary">July 30, 2025</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Betting Rules */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">🎰 Betting Rules</h3>
          <div className="space-y-4 text-gray-300">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-white mb-2">Bet Limits</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Minimum Bet:</span>
                    <span className="text-primary font-bold">10 chips</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Maximum Bet:</span>
                    <span className="text-primary font-bold">50 chips</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bet Range:</span>
                    <span className="text-primary font-bold">10-50 chips</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>



        {/* Game Mechanics */}
        <div className="card">
          <h3 className="text-xl font-bold text-white mb-4">⚙️ Game Mechanics</h3>
          <div className="space-y-4 text-gray-300">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-white mb-2">Prediction Process</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">1.</span>
                    <span>Select a TGE from the available options</span>
                  </div>
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">2.</span>
                    <span>Enter your predicted FDV value</span>
                  </div>
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">3.</span>
                    <span>Choose how many chips to bet (10-50)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">4.</span>
                    <span>Submit your prediction before the deadline</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-bold text-white mb-2">Settlement Process</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">1.</span>
                    <span>Actual FDV is recorded after TGE launch</span>
                  </div>
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">2.</span>
                    <span>Points are calculated based on accuracy</span>
                  </div>
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">3.</span>
                    <span>Leaderboard is updated automatically</span>
                  </div>
                  <div className="flex items-start">
                    <span className="mr-2 mt-1">4.</span>
                    <span>Winners are determined at the end</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default Rules; 